import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, workerNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { MapPin, Calendar, Clock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkerAssignments() {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const assignmentsRes = await axios.get(`${API}/assignments`);
        setAssignments(assignmentsRes.data);
        
        // Fetch project details for each assignment
        const projectIds = [...new Set(assignmentsRes.data.map(a => a.project_id))];
        const projectPromises = projectIds.map(id => axios.get(`${API}/projects/${id}`).catch(() => null));
        const projectResponses = await Promise.all(projectPromises);
        
        const projectMap = {};
        projectResponses.forEach((res, index) => {
          if (res?.data) {
            projectMap[projectIds[index]] = res.data;
          }
        });
        setProjects(projectMap);
      } catch (error) {
        console.error("Error fetching assignments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAcceptAssignment = async (assignmentId) => {
    try {
      await axios.put(`${API}/assignments/${assignmentId}/status`, null, {
        params: { status: "accepted" }
      });
      toast.success(t('success.updated'));
      
      // Refresh
      const res = await axios.get(`${API}/assignments`);
      setAssignments(res.data);
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleCheckIn = async (assignmentId) => {
    try {
      await axios.put(`${API}/assignments/${assignmentId}/status`, null, {
        params: { status: "in_progress" }
      });
      toast.success(t('success.updated'));
      
      const res = await axios.get(`${API}/assignments`);
      setAssignments(res.data);
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  return (
    <DashboardLayout navItems={workerNavItems} title={t('nav.assignments')}>
      <div className="space-y-6" data-testid="worker-assignments">
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">{t('nav.assignments')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">{t('common.noData')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignments.map((assignment) => {
                  const project = projects[assignment.project_id];
                  return (
                    <div
                      key={assignment.assignment_id}
                      className="p-4 bg-white/5 rounded-lg border border-white/10"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-medium text-white">
                            {project?.title || `${t('projects.title')} #${assignment.project_id?.slice(-8)}`}
                          </h3>
                          {project && (
                            <div className="flex items-center gap-4 mt-2 text-sm text-[#94A3B8]">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {project.location_city}
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {assignment.scheduled_date}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium border ${
                              assignment.status === "completed"
                                ? "bg-green-500/20 text-green-400 border-green-500/30"
                                : assignment.status === "in_progress"
                                ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
                                : assignment.status === "accepted"
                                ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                            }`}
                          >
                            {assignment.status?.replace("_", " ")}
                          </span>
                          
                          {assignment.status === "pending" && (
                            <Button
                              onClick={() => handleAcceptAssignment(assignment.assignment_id)}
                              size="sm"
                              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              {t('common.confirm')}
                            </Button>
                          )}
                          
                          {assignment.status === "accepted" && (
                            <Button
                              onClick={() => handleCheckIn(assignment.assignment_id)}
                              size="sm"
                              variant="outline"
                              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                            >
                              <Clock className="w-4 h-4 mr-1" />
                              {t('assignments.checkIn')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
